import {Divider, Skeleton} from '@mui/material'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {OrderCancellationBanner} from '../../../../features/rmc/orderCancellation/components/OrderCancellationBanner'
import {OrderCancellationResultContainer} from '../../../../features/rmc/orderCancellation/components/OrderCancellationResult'
import {useOrderCancellationBanner} from '../../../../features/rmc/orderCancellation/hooks/useOrderCancellationBanner'
import {CancellationRequestStatus} from '../../../../features/rmc/orderCancellation/types/cancellationRequest.types'
import {OrderCancellationButton} from '../../components'
import {useOrderCancellationButtonVisibility} from '../../components/OrderCancellationButton/hooks/useOrderCancellationButtonVisibility'
import {Order} from '../../Order.types'
import {OrderHistoryDetails} from '../OrderHistoryDetails'

import {DetailsField} from './DetailsField'
import {ExportDeliveriesButton} from './ExportDeliveriesButton'
import {getDeliveryInstructions} from './utils'

interface SideOrderDetailsProps {
  order?: Order
  customerId: string
  haulierId: string
  useHaulierView: boolean
  isFetching: boolean
  onOrderDataChanged: () => void
}

export const SideOrderDetails: React.FC<SideOrderDetailsProps> = ({
  order,
  customerId,
  haulierId,
  useHaulierView,
  isFetching,
  onOrderDataChanged
}) => {
  const {t} = useTranslation()
  const isOrderCancellationButtonVisible = useOrderCancellationButtonVisibility(order)
  const [cancellationRequest, setCancellationRequest] = useState<
    CancellationRequestStatus | undefined
  >()
  const cancellationBannerInfo = useOrderCancellationBanner({
    orderId: order?.orderId ?? '',
    orderStatus: order?.orderStatus,
    enabled: !cancellationRequest && isOrderCancellationButtonVisible,
    onOrderDataChanged
  })

  React.useEffect(() => {
    setCancellationRequest(undefined)
  }, [order?.orderId])

  if (isFetching) return <Skeleton variant="rectangular" height={200} />

  if (!order) return null

  if (cancellationRequest) {
    return (
      <OrderCancellationResultContainer
        orderId={order.orderId}
        requestId={cancellationRequest.requestId}
        onViewOrderDetails={() => setCancellationRequest(undefined)}
        onClose={() => setCancellationRequest(undefined)}
      />
    )
  }

  return (
    <>
      {cancellationBannerInfo.showBanner && cancellationBannerInfo.variant && (
        <OrderCancellationBanner
          variant={cancellationBannerInfo.variant}
          onRefresh={cancellationBannerInfo.onRefresh}
          onDismiss={cancellationBannerInfo.onDismiss}
          isRefreshing={cancellationBannerInfo.isRefreshing}
          isDismissing={cancellationBannerInfo.isDismissing}
        />
      )}
      <DetailsField
        label={t('order.orderDetails.deliveryInstructions')}
        dataTestId="delivery-instructions"
        value={getDeliveryInstructions(order)}
      />
      <ExportDeliveriesButton
        order={order}
        customerId={customerId}
        haulierId={haulierId}
        useHaulierView={useHaulierView}
      />
      <OrderHistoryDetails order={order} />
      {isOrderCancellationButtonVisible && (
        <>
          <Divider variant="fullWidth" />
          <OrderCancellationButton
            key={order.orderId}
            order={order}
            disabled={cancellationBannerInfo.hasProcessingRequest}
            onRequestSubmitted={setCancellationRequest}
            cancellationRequestStatus={cancellationBannerInfo.request}
            isErrorCancellationRequestStatus={cancellationBannerInfo.isError}
          />
        </>
      )}
    </>
  )
}
